/**
 * Cookie Settings Modal - Enterprise Edition
 * 
 * Apple-inspired minimalist design in Danish
 * GDPR & EU ePrivacy Directive compliant
 * WCAG 2.1 AA accessible
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Cookie, Shield, CheckCircle2, X } from 'lucide-react';
import { useCookieConsent, CookieConsent } from '@/hooks/useCookieConsent';
import { Link } from 'react-router-dom';

interface CookieModalProps {
  open: boolean;
  onClose: () => void;
}

interface CookieCategoryInfo {
  id: keyof Omit<CookieConsent, 'timestamp' | 'version'>;
  title: string;
  description: string;
  required: boolean;
  examples: string[];
}

const COOKIE_CATEGORIES: CookieCategoryInfo[] = [
  {
    id: 'necessary',
    title: 'Nødvendige cookies',
    description:
      'Disse cookies er nødvendige for, at hjemmesiden fungerer korrekt. De kan ikke slås fra, da de sikrer grundlæggende funktioner som login, sikkerhed og navigation.',
    required: true,
    examples: [
      'Session-cookies (sb-auth-token) - Holder dig logget ind',
      'CSRF-tokens - Beskytter mod sikkerhedstrusler',
      'Cookiesamtykke (cookieConsent_v1) - Husker dine valg',
    ],
  },
  {
    id: 'functional',
    title: 'Præferencecookies',
    description:
      'Disse cookies forbedrer brugeroplevelsen ved at huske dine præferencer som sprog, tema (mørk/lys tilstand) og personlige indstillinger.',
    required: false,
    examples: [
      'Sprogpræference - Husker dit valgte sprog',
      'Tema-indstilling - Husker mørk/lys tilstand',
      'Layout-præferencer - Husker dine visningsindstillinger',
    ],
  },
  {
    id: 'analytics',
    title: 'Statistikcookies',
    description:
      'Disse cookies hjælper os med at forstå, hvordan du bruger hjemmesiden, så vi kan forbedre funktionalitet og brugeroplevelse. Data er anonymiseret og bruges kun internt.',
    required: false,
    examples: [
      'Intern anonymiseret besøgsstatistik - Gemmes kun i vores database',
      'Performance metrics - Indlæsningstider og tekniske fejl',
      'Brugeradfærd - Hvilke sider besøges mest (fuldt anonymt, ingen sporing)',
    ],
  },
  {
    id: 'marketing',
    title: 'Markedsføringscookies',
    description:
      'Disse cookies bruges til at vise relevante annoncer og markedsføringsmateriale. De kan også spore din aktivitet på tværs af hjemmesider.',
    required: false,
    examples: [
      'Tredjepartsannoncører - Målrettet markedsføring',
      'Social media pixels - Sporing til Facebook, LinkedIn',
      'Remarketingkampagner - Viser relevante annoncer',
    ],
  },
];

export function CookieModal({ open, onClose }: CookieModalProps) {
  const { consent, grantConsent, acceptAll, rejectNonEssential } = useCookieConsent();
  
  // Initialize with current consent or defaults
  const [selections, setSelections] = useState({
    necessary: true,
    functional: consent?.functional ?? false,
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
  });

  // Update selections when consent changes
  useEffect(() => {
    if (consent) {
      setSelections({
        necessary: true,
        functional: consent.functional,
        analytics: consent.analytics,
        marketing: consent.marketing,
      });
    }
  }, [consent]);

  const handleToggle = (category: keyof typeof selections) => {
    if (category === 'necessary') return; // Cannot toggle required
    setSelections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSavePreferences = () => {
    grantConsent({
      functional: selections.functional,
      analytics: selections.analytics,
      marketing: selections.marketing,
    });
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleRejectAll = () => {
    rejectNonEssential();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl p-0 gap-0 transition-all duration-300 ease-out animate-in fade-in-0 zoom-in-95"
        aria-describedby="cookie-modal-description"
      >
        {/* Header with Close Button */}
        <div className="relative px-6 py-6 border-b border-app-border">
          <DialogHeader className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-app-primary/10 flex-shrink-0">
                <Cookie className="h-6 w-6 text-app-primary" aria-hidden="true" />
              </div>
              <div className="flex-1 pt-1">
                <DialogTitle className="text-2xl font-semibold text-app-text mb-3">
                  Cookieindstillinger
                </DialogTitle>
                <DialogDescription 
                  id="cookie-modal-description" 
                  className="text-base text-gray-600 dark:text-gray-300 leading-relaxed"
                >
                  Tilpas dine cookiepræferencer. Du kan til enhver tid ændre disse indstillinger.
                </DialogDescription>
              </div>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-app-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus"
                aria-label="Luk"
                type="button"
              >
                <X className="h-5 w-5 text-app-text-secondary" />
              </button>
            </div>
          </DialogHeader>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 260px)' }}>
          <div className="space-y-4">
            {COOKIE_CATEGORIES.map((category) => (
              <CookieCategoryCard
                key={category.id}
                category={category}
                checked={selections[category.id]}
                onToggle={() => handleToggle(category.id)}
              />
            ))}

            {/* GDPR Information */}
            <Alert className="border-app-primary/20 bg-app-primary/5 mt-6">
              <Shield className="h-4 w-4 text-app-primary" aria-hidden="true" />
              <AlertDescription>
                <p className="text-sm font-semibold mb-3 text-app-text">
                  Dine rettigheder efter GDPR:
                </p>
                <ul className="text-sm space-y-2 text-app-text-secondary">
                  <li className="flex items-start gap-2">
                    <span className="text-app-primary mt-0.5">✓</span>
                    <span>Du kan til enhver tid trække dit samtykke tilbage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-app-primary mt-0.5">✓</span>
                    <span>Du kan anmode om en kopi af dine data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-app-primary mt-0.5">✓</span>
                    <span>Du kan anmode om sletning af dine data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-app-primary mt-0.5">✓</span>
                    <span>Du kan klage til Datatilsynet</span>
                  </li>
                </ul>
              </AlertDescription>
            </Alert>

            {/* Legal Footer */}
            <div className="text-sm text-app-text-secondary leading-relaxed space-y-3 pt-4">
              <p>
                Læs mere i vores{' '}
                <Link
                  to="/cookies"
                  className="font-medium text-app-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-1 rounded"
                  onClick={onClose}
                >
                  Cookiepolitik
                </Link>
                {' '}og{' '}
                <Link
                  to="/privatlivspolitik"
                  className="font-medium text-app-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-1 rounded"
                  onClick={onClose}
                >
                  Privatlivspolitik
                </Link>
                .
              </p>
              <p>
                Har du spørgsmål om databeskyttelse? Kontakt os på{' '}
                <a
                  href="mailto:it@tryghaand.dk"
                  className="font-medium text-app-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-1 rounded"
                >
                  it@tryghaand.dk
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-app-border bg-app-secondary/30">
          <DialogFooter className="flex-col-reverse sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="w-full sm:w-auto order-3 sm:order-1"
              aria-label="Afvis alle ikke-nødvendige cookies"
            >
              Afvis alle
            </Button>
            <div className="hidden sm:block sm:flex-1" />
            <Button
              variant="secondary"
              onClick={handleAcceptAll}
              className="w-full sm:w-auto order-2"
              aria-label="Accepter alle cookies"
            >
              Accepter alle
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="w-full sm:w-auto order-1 sm:order-3 bg-app-primary hover:bg-app-primary-hover text-app-primary-text"
              aria-label="Gem mine cookiepræferencer"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Gem indstillinger
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CookieCategoryCardProps {
  category: CookieCategoryInfo;
  checked: boolean;
  onToggle: () => void;
}

function CookieCategoryCard({ category, checked, onToggle }: CookieCategoryCardProps) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="rounded-xl border border-app-border bg-app-surface hover:border-app-primary/30 hover:shadow-sm transition-all duration-200">
      {/* Header with checkbox */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <Checkbox
            id={`cookie-${category.id}`}
            checked={checked}
            onCheckedChange={onToggle}
            disabled={category.required}
            className="mt-1 flex-shrink-0 h-5 w-5"
            aria-describedby={`cookie-${category.id}-description`}
          />
          <div className="flex-1 min-w-0">
            <Label
              htmlFor={`cookie-${category.id}`}
              className={`text-base font-semibold leading-tight block mb-2 ${
                category.required ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              {category.title}
              {category.required && (
                <span className="ml-2 inline-block px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  Altid aktiv
                </span>
              )}
              {!category.required && (
                <span className="ml-2 inline-block px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Valgfri
                </span>
              )}
            </Label>
            <p 
              id={`cookie-${category.id}-description`}
              className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed"
            >
              {category.description}
            </p>
            
            {/* Examples - collapsible */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-3 text-xs font-medium text-app-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-1 rounded inline-flex items-center gap-1"
              aria-expanded={expanded}
              type="button"
            >
              <span className="text-app-primary">{expanded ? '▼' : '▶'}</span>
              {expanded ? 'Skjul eksempler' : 'Se eksempler'}
            </button>
          </div>
        </div>

        {/* Examples - collapsible section */}
        {expanded && (
          <div className="ml-9 mt-3 pt-3 space-y-2 border-t border-app-border/50">
            <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide mb-2">
              Eksempler:
            </p>
            <ul className="text-xs text-app-text-tertiary space-y-2">
              {category.examples.map((example, idx) => (
                <li key={idx} className="flex items-start gap-2 pl-1">
                  <span className="text-app-primary mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="leading-relaxed">{example}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
